/**
 * OUTLYR scroll reveals.
 *
 * Adds .is-revealed to elements as they enter the viewport so CSS can fade
 * and lift them in. Deliberately NOT gated behind prefers-reduced-motion:
 * the brand runs its marquee unconditionally for the same reason, and the
 * store owner develops with Reduce Motion switched on.
 *
 * The scroll container in Horizon is .page-wrapper, not the window, so the
 * observer has to be given that root explicitly on desktop.
 */
(() => {
  const SELECTOR = [
    '.shopify-section .csc-h2',
    '.shopify-section .csc-cards li',
    '.shopify-section .csc-facts li',
    '.shopify-section .csc-band',
    '.shopify-section .csc-name',
    '.shopify-section .csc-anat',
    '.shopify-section .csc-story__col',
    '.shopify-section .csc-heard__row',
    '.shopify-section .csc-narrow',
    '.shopify-section .brand-statement',
    '.resource-list__item',
  ].join(',');

  const init = () => {
    const scroller = document.querySelector('.page-wrapper');
    const targets = document.querySelectorAll(SELECTOR);
    if (!targets.length) return;

    // If IntersectionObserver is unavailable, show everything rather than
    // leaving the page blank.
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const useScroller = scroller && scroller.scrollHeight > scroller.clientHeight;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { root: useScroller ? scroller : null, rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el, i) => {
      el.classList.add('will-reveal');
      // Stagger siblings so grids cascade rather than all landing at once.
      const sibs = el.parentElement ? [...el.parentElement.children] : [];
      const idx = sibs.indexOf(el);
      el.style.setProperty('--reveal-delay', `${Math.min(idx >= 0 ? idx : 0, 5) * 70}ms`);
      observer.observe(el);
    });

    initCounters(useScroller ? scroller : null);
  };

  /**
   * Counts a number up from 0 to its target as it scrolls into view — a
   * different kind of "develops as you scroll" moment than the fade+lift
   * every other element gets, for the one or two stats on a page that
   * deserve to feel like they're actually landing rather than just
   * appearing. Text content stays as the real final value in the HTML
   * (e.g. "80%") so nothing depends on JS for the correct number to show;
   * this only overwrites it temporarily while counting.
   */
  const initCounters = (scroller) => {
    const counters = document.querySelectorAll('[data-count-to]');
    if (!counters.length) return;

    if (!('IntersectionObserver' in window)) return;

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const run = (el) => {
      const target = parseFloat(el.dataset.countTo);
      const suffix = el.dataset.countSuffix || '';
      if (!Number.isFinite(target)) return;

      if (prefersReducedMotion) {
        el.textContent = `${target}${suffix}`;
        return;
      }

      const duration = 1100;
      const start = performance.now();
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const value = Math.round(target * easeOutCubic(progress));
        el.textContent = `${value}${suffix}`;
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          el.textContent = `${target}${suffix}`;
        }
      };
      requestAnimationFrame(step);
    };

    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          run(entry.target);
          counterObserver.unobserve(entry.target);
        });
      },
      { root: scroller, rootMargin: '0px 0px -8% 0px', threshold: 0.4 }
    );

    counters.forEach((el) => counterObserver.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Sections re-render in the theme editor; re-scan when they do.
  document.addEventListener('shopify:section:load', init);
})();
